import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * Pending hover-intent deactivation timer. Gives the pointer time to cross the
   * gap between a top-level item and its (left-aligned, full-width) submenu
   * without the menu collapsing. Cleared on re-activation.
   * @type {ReturnType<typeof setTimeout> | null}
   */
  #deactivateTimer = null;
  #activateTimer = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    document.addEventListener('keydown', this.#documentKeydown);
    document.addEventListener('focusin', this.#documentFocusIn);
    document.addEventListener('pointerdown', this.#documentPointerDown, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    document.removeEventListener('keydown', this.#documentKeydown);
    document.removeEventListener('focusin', this.#documentFocusIn);
    clearTimeout(this.#activateTimer);
    document.removeEventListener('pointerdown', this.#documentPointerDown, true);
    clearTimeout(this.#deactivateTimer);
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);


  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  pointerEnter = (event) => {
    if (event.pointerType === 'touch') return;
    clearTimeout(this.#activateTimer);
    clearTimeout(this.#deactivateTimer);
    if (findMenuItem(event.target) === this.#state.activeItem) return;
    this.#activateTimer = setTimeout(() => {
      this.activate(event);
    }, 140);
  };

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    // Cancel any pending deactivation so moving from a top-level item into its
    // submenu (across the small gap below the header) keeps the menu open.
    clearTimeout(this.#activateTimer);
    clearTimeout(this.#deactivateTimer);
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = item.closest('.menu-list__list-item')?.slot !== 'overflow';
    if (isDefaultSlot && !findSubmenu(item)) {
      this.#deactivate(undefined, true);
      return;
    }

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      this.#setExpandedState(previouslyActiveItem, false);
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    this.#setExpandedState(item, true);

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Toggle a top-level disclosure from its dedicated button.
   * Keeping this separate from the destination link makes the entire chevron
   * target predictable on pointer, touch, and keyboard input.
   * @param {PointerEvent | KeyboardEvent} event
   */
  toggle = (event) => {
    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);
    if (!item) return;

    clearTimeout(this.#activateTimer);
    clearTimeout(this.#deactivateTimer);
    if (item === this.#state.activeItem) {
      this.#deactivate(item, true);
      return;
    }

    this.activate(event);
  };

  deactivate(event) {
    clearTimeout(this.#activateTimer);
    const item = this.#state.activeItem;
    const row = item?.closest('.menu-list__list-item');
    const submenu = findSubmenu(item);
    if (event.relatedTarget instanceof Node && row?.contains(event.relatedTarget)) return;
    if (submenu?.contains(document.activeElement)) return;
    clearTimeout(this.#deactivateTimer);
    this.#deactivateTimer = setTimeout(() => this.#deactivate(), 240);
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem, force = false) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu, overflow list, or the open submenu
    // itself is still being hovered (covers the diagonal move into a submenu).
    const activeSubmenu = findSubmenu(item);
    if (!force && (this.overflowListHovered || this.overflowMenu?.matches(':hover') || activeSubmenu?.matches(':hover'))) return;

    clearTimeout(this.#deactivateTimer);
    this.#deactivateTimer = null;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    this.#setExpandedState(item, false);

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  /**
   * Close an open disclosure without requiring a pointer to leave the header.
   * @param {KeyboardEvent} event
   */
  #documentKeydown = (event) => {
    if (event.key !== 'Escape') return;
    clearTimeout(this.#activateTimer);
    if (!this.#state.activeItem) return;

    const activeItem = this.#state.activeItem;
    event.preventDefault();
    this.#deactivate(activeItem, true);
    const control = activeItem.closest('.menu-list__list-item')?.querySelector('.menu-list__toggle');
    (control || activeItem).focus();
  };

  /**
   * Close an open disclosure when the shopper begins an interaction elsewhere.
   * @param {PointerEvent} event
   */
  #documentPointerDown = (event) => {
    if (!(event.target instanceof Node) || this.contains(event.target)) return;
    clearTimeout(this.#activateTimer);
    this.#deactivate(undefined, true);
  };

  #documentFocusIn = (event) => {
    const row = this.#state.activeItem?.closest('.menu-list__list-item');
    if (row && event.target instanceof Node && !row.contains(event.target)) {
      this.#deactivate(undefined, true);
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }

  /**
   * Keep the visible menu link and its adjacent disclosure control in sync.
   * @param {HTMLElement} item
   * @param {boolean} expanded
   */
  #setExpandedState(item, expanded) {
    const value = expanded ? 'true' : 'false';
    item.ariaExpanded = value;
    const toggle = item.closest('.menu-list__list-item')?.querySelector('.menu-list__toggle');
    if (!toggle) return;

    toggle.setAttribute('aria-expanded', value);
    const label = toggle.dataset.menuLabel || item.textContent?.trim() || 'menu';
    toggle.setAttribute('aria-label', `${expanded ? 'Close' : 'Open'} ${label} menu`);
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"]')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element.closest('.menu-list__list-item')?.querySelector('[ref="menuitem"]') ?? null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.closest('.menu-list__list-item')?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
